import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, Drawer, Typography, Avatar, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import Scrollbar from '../../components/Scrollbar';
import { MHidden } from '../../components/@material-extend';
import sidebarConfig from '../../SidebarConfig';
import useCityStore from '../../../infrastructure/store/useCityStore';

const DRAWER_WIDTH = 280;

const regionToFileNameMap = {
  '彰化縣': 'Changhua',
  '雲林縣': 'Yunlin',
  '台南市': 'Tainan',
  '高雄市': 'Kaohsiung',
  '澎湖縣': 'Penghu',
  '林園市': 'Linyuan',
  '嘉義市': 'Chiayi',
  '台北市': 'Taipei'
};

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH
  },
  backgroundColor: 'rgba(200, 230, 255, 0.1)', // 添加這行
}));

const SidebarHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.divider}`
}));

const LogoAndTitleContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const TitleContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: '15px', // 增加左邊距
});

const UserInfo = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  borderTop: `1px solid ${theme.palette.divider}`
}));

const NavItem = styled(ListItem)(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  color: theme.palette.text.primary, // 改為主要文字顏色
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)', // 淡白色懸停效果
  },
  '&.active': {
    color: theme.palette.primary.main,
    backgroundColor: 'rgba(255, 255, 255, 0.2)', // 稍微深一點的背景色表示選中狀態
    fontWeight: 'fontWeightBold',
  },
}));

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const citySettings = useCityStore(state => state.citySettings);
  const [email, setEmail] = useState(localStorage.getItem('email') || '');
  const [region, setRegion] = useState(localStorage.getItem('region') || '');
  const displayName = useMemo(() => {
    // 如果有 citySettings 資料
    if (citySettings.length > 0) {
      // 尋找符合目前 region 的設定
      const matchedCity = citySettings.find(city => city.region === region);
      // 如果找到匹配的城市，返回其 display_name
      // 否則返回預設值
      return matchedCity ? matchedCity.display_name : '智慧城市移動資訊平台';
    }
    return '智慧城市移動資訊平台';
  }, [citySettings, region]);
  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
  }, [pathname, isOpenSidebar, onCloseSidebar]);

  useEffect(() => {
    const handleStorageChange = () => {
      setEmail(localStorage.getItem('email') || '');
      setRegion(localStorage.getItem('region') || '');
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // 動態選擇 logo
  const logoSrc = useMemo(() => {
    if (region) {
      const fileName = regionToFileNameMap[region] || 'default-logo';
      return `${process.env.PUBLIC_URL}/images/${fileName}.jfif`;
    }
    return `${process.env.PUBLIC_URL}/images/default-logo.png`; // 預設 logo
  }, [region]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: '100%',
        '& .simplebar-content': {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'transparent', // 確保滾動條內容區域是透明的
        }
      }}
    >
      <SidebarHeader>
        <LogoAndTitleContainer>
          <Box
            component="img"
            src={logoSrc}
            alt="Logo"
            sx={{
              width: 80, // 增加 logo 寬度
              height: 80, // 增加 logo 高度
              objectFit: 'contain',
            }}
          />
          <TitleContainer>
            <Typography variant="subtitle1" sx={{ lineHeight: 1.2, fontWeight: 'bold', fontSize: '1.1rem' }}> {/* 增加字體大小 */}
              {displayName.split('移動資訊平台')[0]}
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.2, fontSize: '1.1rem' }}> {/* 增加字體大小 */}
              移動資訊平台
            </Typography>
          </TitleContainer>
        </LogoAndTitleContainer>
      </SidebarHeader>

      <List>
        {sidebarConfig.map((item) => (
          <NavItem
            key={item.title}
            component={RouterLink}
            to={item.path}
            sx={{
              '&.active': {
                color: 'primary.main',
                bgcolor: 'action.selected',
                fontWeight: 'fontWeightBold',
              },
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText disableTypography primary={item.title} />
          </NavItem>
        ))}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      <UserInfo>
        <Avatar sx={{ mr: 2 }}>{email.charAt(0).toUpperCase()}</Avatar>
        <Typography variant="subtitle2">{email}</Typography>
      </UserInfo>
    </Scrollbar>
  );

  return (
    <RootStyle>
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'rgba(200, 230, 255, 0.1)', // 與 RootStyle 相同的背景色
              borderRightStyle: 'dashed'
            }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}